import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { api } from '../api/client';
import { seedCollectionIdentities } from '../hooks/useCollectionIdentity';
import { API_COLLECTIONS_URL } from '../utils';
import { normalizeId } from '../utils/ids';
import { readSavedCopy, writeSavedCopy } from '../utils/savedCopy';
import { getSessionKey, getSessionToken, getSessionUserId, useOnSessionChange } from '../utils/session';

/**
 * App-wide list of the collections the signed-in account can read — one
 * `GET /api/collections` per session, shared by the rail, the command palette,
 * the collection switcher and every picker that used to ask for itself
 * (KPR-1078). Collections change rarely; what changes one (a create, a rename,
 * an archive, a transfer, a move under a parent) calls `refresh()`, and a
 * picker that must not offer a stale row asks again when it opens (cheap: the
 * API answers an unchanged list with a 304, KPR-1079).
 *
 * The latch is CardDefinitionsContext's: keyed on the session, and only a
 * SUCCESSFUL fetch latches it, so a failed one (a deploy restart, a 429) is
 * retried by the next consumer instead of leaving the session with an empty
 * rail. Signed out it settles EMPTY and asks nothing; another account signing
 * in on the same tab drops the last one's list and loads its own.
 *
 * Every answer also teaches the qualified badge's identity cache
 * (hooks/useCollectionIdentity), as the rail and the palette each did alone.
 *
 * A SAVED COPY of the last successful load (utils/savedCopy.js, KPR-1080) seeds
 * the list on a full page load, so the rail has its rows before the fetch
 * lands. The fetch always runs and replaces it; `loaded` stays false until it
 * succeeds; a failed fetch keeps the copy on screen. `hydrated` says the rows
 * shown are the copy, and `available` (loaded or hydrated) is what a surface
 * that just wants rows to draw reads.
 *
 * Exposes: collections, collectionsById, loading (until the first fetch
 * settles — false at once while a saved copy is shown), hydrated, loaded (a fetch has SUCCEEDED for this session), error, version
 * (bumps on every successful load), ensureLoaded(), refresh().
 */
const CollectionsContext = createContext(null);
const NO_ROWS = Object.freeze([]);

// What a write calls: services and forms have no hook, so the mounted provider
// registers its refresh here and `noteCollectionsChanged()` reaches it.
const changeListeners = new Set();

/**
 * "A collection was created, renamed, archived, transferred, deleted, moved
 * under a parent or installed into": ask for the list again so no surface
 * shows a stale name. A no-op while nothing is mounted.
 */
export function noteCollectionsChanged() {
	changeListeners.forEach(listener => listener());
}

const readToken = () => getSessionToken();

const SAVED_NAME = 'collections';
const readSaved = () => {
	const copy = readSavedCopy(SAVED_NAME);
	return Array.isArray(copy) ? copy : null;
};

export function CollectionsProvider({ children }) {
	const [saved] = useState(readSaved);
	const [collections, setCollections] = useState(() => saved || NO_ROWS);
	const [hydrated, setHydrated] = useState(Boolean(saved));
	const [settled, setSettled] = useState(false);
	const [fetching, setFetching] = useState(false);
	const [error, setError] = useState(null);
	const [loaded, setLoaded] = useState(false);
	const [version, setVersion] = useState(0);
	// The copy teaches the badge resolver its names at once; the fetch's answer
	// then replaces them whole.
	useEffect(() => {
		if (saved) seedCollectionIdentities(saved, { session: getSessionKey() });
	}, [saved]);
	const loadedForRef = useRef(null); // { token } once a fetch SUCCEEDED for that token
	const inFlightRef = useRef(undefined); // { token, promise } of the request in flight
	// Requests are numbered as they leave; an answer applies only if no
	// LATER-numbered answer has applied already (KPR-1236). A refresh on a
	// picker's open can start while the first load is still out, and the slower
	// of the two must never put its older list over the newer one.
	const startedRef = useRef(0);
	const appliedRef = useRef(0);

	const load = useCallback(async ({ force = false } = {}) => {
		if (!getSessionUserId()) {
			setSettled(true); // signed out: settled, empty, nothing asked
			return undefined;
		}
		const token = readToken();
		if (!force) {
			if (loadedForRef.current && loadedForRef.current.token === token) return undefined; // fresh
			if (inFlightRef.current && inFlightRef.current.token === token) return inFlightRef.current.promise;
		}
		if (loadedForRef.current && loadedForRef.current.token !== token) {
			loadedForRef.current = null; // another account: the last one's list must not linger
			setCollections(NO_ROWS);
			setHydrated(false);
			setLoaded(false);
		}
		const session = getSessionKey();
		const sequence = startedRef.current + 1;
		startedRef.current = sequence;
		setFetching(true);
		setError(null);
		const promise = (async () => {
			try {
				const { data } = await api.get(API_COLLECTIONS_URL);
				if (readToken() !== token) return undefined; // session changed mid-flight; its own fetch wins
				const rows = Array.isArray(data) ? data : [];
				if (sequence < appliedRef.current) return rows; // a newer answer is already in place
				appliedRef.current = sequence;
				seedCollectionIdentities(rows, { complete: true, session });
				setCollections(rows);
				setHydrated(false);
				writeSavedCopy(SAVED_NAME, rows);
				loadedForRef.current = { token };
				setLoaded(true);
				setVersion(v => v + 1);
				return rows;
			} catch (err) {
				if (readToken() === token) setError(err);
				return undefined;
			} finally {
				if (inFlightRef.current && inFlightRef.current.promise === promise) {
					inFlightRef.current = undefined;
					setFetching(false);
				}
				setSettled(true);
			}
		})();
		inFlightRef.current = { token, promise };
		return promise;
	}, []);

	// Another account (or none) on this tab: drop the last one's list at once;
	// the next account's loads here (signed out, load() only settles empty).
	useOnSessionChange(() => {
		loadedForRef.current = null;
		inFlightRef.current = undefined;
		setCollections(NO_ROWS);
		setHydrated(false);
		setLoaded(false);
		setFetching(false);
		setError(null);
		load();
	});

	const refresh = useCallback(() => load({ force: true }), [load]);
	useEffect(() => {
		changeListeners.add(refresh);
		return () => { changeListeners.delete(refresh); };
	}, [refresh]);

	const collectionsById = useMemo(() => {
		const map = {};
		collections.forEach(collection => {
			const id = normalizeId(collection?._id);
			if (id) map[id] = collection;
		});
		return map;
	}, [collections]);

	const value = useMemo(() => ({
		collections,
		collectionsById,
		loading: (fetching || !settled) && !hydrated,
		hydrated,
		loaded,
		available: loaded || hydrated,
		error,
		version,
		ensureLoaded: load,
		refresh,
	}), [collections, collectionsById, fetching, settled, hydrated, loaded, error, version, load, refresh]);

	return <CollectionsContext.Provider value={value}>{children}</CollectionsContext.Provider>;
}

// What a consumer outside a provider reads — a test that renders one surface,
// a page no provider reaches: no collections, nothing asked, nothing to refresh.
const INERT = Object.freeze({
	collections: NO_ROWS,
	collectionsById: Object.freeze({}),
	loading: false,
	loaded: false,
	hydrated: false,
	available: false,
	error: null,
	version: 0,
	ensureLoaded: () => undefined,
	refresh: () => undefined,
});

/**
 * The shared list. Every consumer mount calls ensureLoaded: a no-op while the
 * list is fresh for this session, a (re)fetch after a failure or a sign-in.
 * `refreshOnOpen`: also ask again once when it turns true (a picker's open) so
 * the rows are never staler than the moment the person looked — the cached
 * rows show at once and the answer replaces them. Outside a provider it reads
 * as no collections and asks nothing.
 */
export function useCollections({ refreshOnOpen = false } = {}) {
	const ctx = useContext(CollectionsContext) || INERT;
	const { ensureLoaded, refresh, loaded } = ctx;
	useEffect(() => {
		ensureLoaded();
	}, [ensureLoaded]);
	const loadedRef = useRef(loaded);
	loadedRef.current = loaded;
	const opened = useRef(false);
	useEffect(() => {
		// Nothing held yet: the ensureLoaded above is the read. Rows held: show
		// them now and ask again.
		if (refreshOnOpen && !opened.current && loadedRef.current) refresh();
		opened.current = refreshOnOpen;
	}, [refreshOnOpen, refresh]);
	return ctx;
}

